/**
 * The inline bundle builder.
 *
 * A convenience over the discount, never a participant in it. It adds real
 * variant ids and nothing else: no line item properties, no prices, no claims
 * the Function would have to trust. The saving it shows is a preview computed
 * from the published rate; the Function recomputes everything from the cart
 * itself, so a tampered preview changes what a shopper sees and nothing about
 * what they are charged. If this whole file failed to load, a shopper adding
 * the same items by hand would still get the discount.
 *
 * Products come from each collection's own JSON endpoint in the collection's
 * own sort order, so "the first eight" is a shortlist the merchandising team
 * already curates in Shopify, per store, with no extra tooling.
 */

class BundleList extends HTMLElement {
  connectedCallback() {
    this.shortlist = Number(this.dataset.shortlist) || 8;
    this.currency = this.dataset.currency || "GBP";
    this.money = new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
      style: "currency",
      currency: this.currency,
    });

    this.querySelectorAll(".bundle-card").forEach((card) => this.wireCard(card));
    this.wireTilt();

    // Arriving from a product page: open that bundle with that product already
    // in its slot, rather than making the shopper start again.
    const params = new URLSearchParams(location.search);
    const wanted = params.get("bundle");
    if (wanted) {
      this.seed(wanted, Number(params.get("slot") ?? 0), Number(params.get("variant") ?? 0));
    }
  }

  async seed(slug, slotIndex, variantId) {
    const card = this.querySelector(`.bundle-card[data-bundle="${CSS.escape(slug)}"]`);
    if (!card) return;

    await this.open(card, card.bundleState);
    card.scrollIntoView({ behavior: "smooth", block: "start" });
    if (!variantId) return;

    // The product may sit beyond the shortlist, so look through everything the
    // slot loaded. If it is not there at all, the bundle is still open and the
    // shopper simply chooses for themselves.
    const products = card.bundleState.products?.[slotIndex] || [];
    for (const p of products) {
      const variant = (p.variants || []).find((v) => v.id === variantId);
      if (!variant) continue;
      this.choose(card, card.bundleState, slotIndex, {
        variantId: variant.id,
        title: p.title + (variant.title && variant.title !== "Default Title" ? ` (${variant.title})` : ""),
        price: Number(variant.price),
        image: p.images?.[0]?.src || "",
      });
      return;
    }
  }

  /**
   * The tilt from the birthday sale block, so a bundle tile behaves like the
   * category tiles already on the live sites. Pointer only:
   * there is no cursor to follow on a touch screen, and the transform would
   * just fight the scroll.
   */
  wireTilt() {
    if (!window.matchMedia?.("(hover: hover)").matches) return;
    this.querySelectorAll(".bundle-card__open").forEach((tile) => {
      tile.addEventListener("mousemove", (e) => {
        if (tile.closest(".bundle-card").classList.contains("is-open")) return;
        const r = tile.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width;
        const y = (e.clientY - r.top) / r.height;
        tile.style.transform =
          `perspective(1000px) rotateX(${(0.5 - y) * 6}deg) rotateY(${(x - 0.5) * 8}deg) translateY(-6px)`;
      });
      tile.addEventListener("mouseleave", () => {
        tile.style.transform = "perspective(1000px) rotateX(0) rotateY(0) translateY(0)";
      });
    });
  }

  wireCard(card) {
    const config = JSON.parse(card.dataset.config);
    const toggle = card.querySelector(".bundle-card__open");
    const builder = card.querySelector(".bundle-builder");

    // One chosen variant list per slot.
    const state = {
      config,
      chosen: config.slots.map(() => []),
      // A completed slot collapses so the shopper moves on. Reopening is
      // deliberate, and survives until the slot is incomplete again.
      reopened: config.slots.map(() => false),
      loaded: false,
    };
    card.bundleState = state;

    toggle.addEventListener("click", () => {
      if (card.classList.contains("is-open")) this.close(card);
      else this.open(card, state, true);
    });

    card.querySelector(".bundle-builder__add").addEventListener("click", () => this.addToCart(card, state));
  }

  async open(card, state, moveFocus = false) {
    card.classList.add("is-open");
    this.flatten(card);
    card.querySelector(".bundle-card__open").setAttribute("aria-expanded", "true");
    const builder = card.querySelector(".bundle-builder");
    builder.hidden = false;

    if (!state.loaded) {
      state.loaded = true;
      await this.renderSlots(card, state);
    }

    // Focus the first slot heading so a keyboard or screen reader user lands
    // inside what just opened rather than after it.
    if (moveFocus) {
      const first = builder.querySelector(".bundle-slot__label");
      if (first) {
        first.setAttribute("tabindex", "-1");
        first.focus();
      }
    }
  }

  flatten(card) {
    const tile = card.querySelector(".bundle-card__open");
    if (tile) tile.style.transform = "perspective(1000px) rotateX(0) rotateY(0) translateY(0)";
  }

  close(card) {
    card.classList.remove("is-open");
    card.querySelector(".bundle-card__open").setAttribute("aria-expanded", "false");
    card.querySelector(".bundle-builder").hidden = true;
  }

  /** Every page of a collection's own JSON, in the collection's own order. */
  async wholeCollection(handle) {
    const products = [];
    for (let page = 1; page <= 12; page++) {
      const body = await fetch(`/collections/${handle}/products.json?limit=250&page=${page}`)
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .catch(() => ({ products: [] }));
      const batch = body.products || [];
      products.push(...batch);
      if (batch.length < 250) break;
    }
    return { products };
  }

  /**
   * The display name for a group of options. Product types are warehouse
   * language: a shopper looking for an evening pair is looking for SunDown,
   * not for "Blue Light Blocking Glasses - Amber Lens". This lookup is the
   * only brand knowledge in this file, and an unknown type falls through to
   * itself rather than breaking.
   */
  groupLabel(productType) {
    const t = (productType || "").toLowerCase();
    if (t.includes("computer glasses") && t.includes("clear")) return "ScreenTime";
    if (t.includes("amber lens")) return "SunDown";
    if (t.includes("red lens")) return "NightFall";
    if (t.includes("yellow lens")) return "DayMax";
    return productType || "Other";
  }

  /**
   * Split a slot's pool into groups, each with its standard products and its
   * readers/prescription products held back.
   *
   * Prescription is a lens option on a frame, not a product to scroll past.
   * On the evening slot it was 36 of 59 entries, so the 23 frames a shopper
   * is actually choosing between were buried.
   */
  groupProducts(products) {
    const groups = [];
    const byKey = new Map();
    for (const p of products) {
      const key = this.groupLabel(p.product_type);
      if (!byKey.has(key)) {
        const g = { key, label: key, standard: [], rx: [] };
        byKey.set(key, g);
        groups.push(g);
      }
      byKey.get(key)[p._rx ? "rx" : "standard"].push(p);
    }
    // A group with nothing but prescription in it would otherwise look empty
    // until the toggle is found.
    for (const g of groups) {
      if (g.standard.length === 0 && g.rx.length > 0) {
        g.standard = g.rx;
        g.rx = [];
      }
    }
    return groups;
  }

  /** Everything a slot can legitimately offer, in merchandised order. */
  async slotProducts(slot, bundleExcluded = []) {
    const handles = slot.collectionHandles || [];
    // Both levels of exclusion, because the Function applies both: per slot
    // for the kids frames that carry the same four product types as the adult
    // ones, and per bundle for the two panels Morning Recovery cannot afford
    // to discount. Offering either one builds a basket the cart then refuses.
    const excluded = new Set([...(slot.excludeProductHandles || []), ...bundleExcluded]);
    // The whole collection, not the first shortlist*3 of it. Fetching 24 and
    // then grouping would group a truncated list: readers-and-prescriptions
    // holds 140 products, so 116 of them were never offered and nobody could
    // have noticed, because the cart would have discounted them happily.
    const pages = await Promise.all(handles.map((handle) => this.wholeCollection(handle)));

    const seen = new Set();
    const products = [];
    const byHandle = new Map();
    pages.forEach((page, idx) => {
      byHandle.set(handles[idx], new Set((page.products || []).map((p) => p.id)));
    });
    const isRx = (h) => /readers|prescription/.test(h);
    const rxHandles = handles.filter(isRx);
    const plainHandles = handles.filter((h) => !isRx(h));
    for (const page of pages) {
      for (const p of page.products || []) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        // Gift cards never count towards a bundle, so never offer one.
        if ((p.product_type || "").toLowerCase().includes("gift card")) continue;
        if (excluded.has(p.handle)) continue;

        // A pinned SKU ADDS a product, it does not filter the others out.
        // engine.js returns true on a SKU match before it looks at
        // collections at all; this used to require the SKU as well as the
        // collection, which left "choose 3 lights" offering three book
        // lights out of thirty-one, and the cart happily discounting the
        // twenty-eight the builder would not show.
        const inCollection = handles.some((h) => (byHandle.get(h) || new Set()).has(p.id));
        const pinned = (p.variants || []).some((v) => (slot.skus || []).includes(v.sku));
        if (!inCollection && !pinned) continue;
        if (!pinned && slot.productTypes && slot.productTypes.length > 0) {
          if (!slot.productTypes.includes(p.product_type)) continue;
        }
        if (!(p.variants || []).some((v) => v.available)) continue;
        // Prescription only when it comes from a prescription collection and
        // from nowhere else. A frame that sits in both is a frame.
        p._rx = rxHandles.some((h) => (byHandle.get(h) || new Set()).has(p.id)) &&
          !plainHandles.some((h) => (byHandle.get(h) || new Set()).has(p.id));
        products.push(p);
      }
    }
    return products;
  }

  async renderSlots(card, state) {
    const host = card.querySelector(".bundle-builder__slots");
    host.innerHTML = state.config.slots
      .map(
        (slot, i) => `
        <section class="bundle-slot" data-slot="${i}">
          <header class="bundle-slot__header">
            <h4 class="bundle-slot__label">${escapeHtml(slot.label)}</h4>
            <p class="bundle-slot__count" data-count>Choose ${slot.quantity}</p>
            <button class="bundle-slot__change" type="button" data-change hidden>Change</button>
          </header>
          <ul class="bundle-slot__chosen" data-chosen role="list"></ul>
          <div class="bundle-slot__options" data-options>
            <p class="bundle-slot__loading">Loading options</p>
          </div>
        </section>`,
      )
      .join("");

    await Promise.all(
      state.config.slots.map(async (slot, i) => {
        const products = await this.slotProducts(slot, state.config.excludeProductHandles || []);
        state.products = state.products || [];
        state.products[i] = products;
        const section = host.querySelector(`[data-slot="${i}"]`);
        const options = section.querySelector("[data-options]");

        if (products.length === 0) {
          options.innerHTML = `<p class="bundle-slot__loading">Nothing available for this slot right now.</p>`;
          return;
        }

        const groups = this.groupProducts(products);
        const ctx = { groups, g: 0, rx: false, slot, options };

        options.innerHTML = `
          ${
            groups.length > 1
              ? `<div class="bundle-groups" role="tablist" aria-label="Narrow the options">
                   ${groups
                     .map(
                       (grp, k) => `<button class="bundle-group" type="button" role="tab"
                          data-group="${k}" aria-selected="${k === 0}">
                          ${escapeHtml(grp.label)}
                          <span class="bundle-group__n">${grp.standard.length + grp.rx.length}</span>
                        </button>`,
                     )
                     .join("")}
                 </div>`
              : ""
          }
          <div class="bundle-rail">
            <button class="bundle-rail__arrow bundle-rail__arrow--prev" type="button"
              data-scroll="-1" aria-label="Scroll left">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <ul class="bundle-slot__grid" role="list" data-track></ul>
            <button class="bundle-rail__arrow bundle-rail__arrow--next" type="button"
              data-scroll="1" aria-label="Scroll right">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="bundle-slot__actions">
            <button class="bundle-slot__rx" type="button" data-rx hidden></button>
            <button class="bundle-slot__more" type="button" data-more hidden></button>
          </div>`;

        const track = options.querySelector("[data-track]");
        const rxButton = options.querySelector("[data-rx]");
        const moreButton = options.querySelector("[data-more]");

        // One function fills the rail, so switching group, revealing
        // prescription and browsing the rest cannot drift apart.
        const fill = (limit, scrollTo) => {
          const grp = ctx.groups[ctx.g];
          const pool = ctx.rx ? grp.standard.concat(grp.rx) : grp.standard;
          const cap = limit ?? this.shortlist;
          track.innerHTML = pool.slice(0, cap).map((x) => this.productTile(x, ctx.slot)).join("");
          // Revealing prescription used to append it past position 8 in a rail
          // that only showed 8, so the button appeared to do nothing at all.
          const target = scrollTo != null && track.children[scrollTo];
          track.scrollLeft = target ? target.offsetLeft - track.offsetLeft : 0;

          rxButton.hidden = grp.rx.length === 0;
          rxButton.textContent = ctx.rx
            ? "Hide readers and prescription"
            : `Readers and prescription (${grp.rx.length})`;

          moreButton.hidden = pool.length <= cap || cap === Infinity;
          moreButton.textContent = `Browse all ${pool.length}`;
          moreButton.dataset.all = String(pool.length);

          this.refresh(card, state);
          this.updateArrows(options);
        };

        options.querySelectorAll("[data-group]").forEach((button) => {
          button.addEventListener("click", () => {
            ctx.g = Number(button.dataset.group);
            ctx.rx = false;
            options.querySelectorAll("[data-group]").forEach((b) => {
              b.setAttribute("aria-selected", String(b === button));
            });
            fill();
          });
        });

        rxButton.addEventListener("click", () => {
          ctx.rx = !ctx.rx;
          const grp = ctx.groups[ctx.g];
          // On, show the whole group and scroll to where prescription starts.
          // Off, back to the shortlist.
          fill(ctx.rx ? Infinity : undefined, ctx.rx ? grp.standard.length : undefined);
        });

        moreButton.addEventListener("click", () => fill(Number(moreButton.dataset.all)));

        section.querySelector("[data-change]").addEventListener("click", () => {
          state.reopened[i] = true;
          this.refresh(card, state);
        });

        fill();

        options.querySelectorAll("[data-scroll]").forEach((button) => {
          button.addEventListener("click", () => {
            const direction = Number(button.dataset.scroll);
            track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: "smooth" });
          });
        });
        track.addEventListener("scroll", () => this.updateArrows(options), { passive: true });
        // Width changes as images load, as the card opens, and as the window
        // resizes. Measuring once was the bug, so this watches instead.
        if (typeof ResizeObserver !== "undefined") {
          new ResizeObserver(() => this.updateArrows(options)).observe(track);
        }
        window.addEventListener("resize", () => this.updateArrows(options), { passive: true });
        requestAnimationFrame(() => this.updateArrows(options));

        options.addEventListener("click", (e) => {
          const button = e.target.closest(".bundle-product__pick");
          if (!button) return;
          const tile = button.closest(".bundle-product");
          const select = tile.querySelector(".bundle-product__variant");
          const option = select?.selectedOptions?.[0];
          this.choose(card, state, i, {
            variantId: Number(option ? option.value : button.dataset.variant),
            title: button.dataset.title + (option ? ` (${option.textContent})` : ""),
            price: Number(option ? option.dataset.price : button.dataset.price),
            // Carried so the saving preview can work off the pre-sale price
            // rather than today's, which is what the Function does.
            compareAtPrice: Number(option ? option.dataset.compare : button.dataset.compare) || 0,
            image: button.dataset.image || "",
          });
        });
      }),
    );

    this.refresh(card, state);
  }

  /**
   * Most BBL products have a single variant, because colour and frame are
   * separate products. The ones that do not, bulb fittings and prescription
   * strengths, get a native select rather than a second screen.
   */
  productTile(p, slot) {
    let variants = (p.variants || []).filter((v) => v.available);
    // Narrow to the pinned sizes only for a product that IS pinned, or a
    // product that merely came from the collections has every one of its
    // variants filtered away and the tile renders as an empty string. That is
    // what left "choose 3 lights" showing groups with nothing in them.
    const pinnedHere = (slot?.skus || []).length > 0 &&
      variants.some((v) => slot.skus.includes(v.sku));
    if (pinnedHere) variants = variants.filter((v) => slot.skus.includes(v.sku));
    if (variants.length === 0) return "";

    const image = p.images?.[0]?.src || "";
    const id = `v-${p.id}`;

    return `
      <li class="bundle-product" data-product="${p.id}">
        <a class="bundle-product__link" href="/products/${p.handle}" target="_blank" rel="noopener">
          ${image ? `<img class="bundle-product__image" src="${sized(image, 240)}" alt="" loading="lazy" width="240" height="240">` : ""}
          <span class="bundle-product__title">${escapeHtml(p.title)}</span>
          <span class="bundle-product__view">View</span>
        </a>
        ${
          variants.length > 1
            ? `<label class="bundle-product__variant-label" for="${id}">
                 <span class="visually-hidden">Option for ${escapeHtml(p.title)}</span>
                 <select class="bundle-product__variant" id="${id}">
                   ${variants.map((v) => `<option value="${v.id}" data-price="${v.price}" data-compare="${v.compare_at_price || ""}">${escapeHtml(v.title)}</option>`).join("")}
                 </select>
               </label>`
            : ""
        }
        <button class="bundle-product__pick" type="button"
          data-variant="${variants[0].id}"
          data-title="${escapeHtml(p.title)}"
          data-price="${variants[0].price}"
          data-compare="${variants[0].compare_at_price || ""}"
          data-image="${image}"
          aria-pressed="false">Add</button>
      </li>`;
  }

  /** Arrows only offer what the rail can actually do. */
  updateArrows(options) {
    const track = options.querySelector("[data-track]");
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const prev = options.querySelector(".bundle-rail__arrow--prev");
    const next = options.querySelector(".bundle-rail__arrow--next");
    // Disabled is the only thing that hides an arrow. A separate "nothing to
    // scroll" class was a second way to lose them, and it never re-measured,
    // so one early measurement taken before layout settled hid them forever.
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  }

  choose(card, state, slotIndex, pick) {
    const slot = state.config.slots[slotIndex];
    const chosen = state.chosen[slotIndex];
    // Filling a full slot replaces the oldest choice rather than refusing,
    // so a shopper changing their mind never has to hunt for a remove button.
    if (chosen.length >= slot.quantity) chosen.shift();
    chosen.push(pick);
    this.refresh(card, state);
  }

  remove(card, state, slotIndex, position) {
    state.chosen[slotIndex].splice(position, 1);
    this.refresh(card, state);
  }

  refresh(card, state) {
    const host = card.querySelector(".bundle-builder__slots");

    state.config.slots.forEach((slot, i) => {
      const section = host.querySelector(`[data-slot="${i}"]`);
      if (!section) return;
      const chosen = state.chosen[i];

      const complete = chosen.length >= slot.quantity;
      if (!complete) state.reopened[i] = false;
      const collapsed = complete && !state.reopened[i];

      section.querySelector("[data-count]").textContent = complete
        ? `${slot.quantity} chosen`
        : `Choose ${slot.quantity - chosen.length} more`;
      section.classList.toggle("is-complete", complete);
      section.classList.toggle("is-collapsed", collapsed);

      const options = section.querySelector("[data-options]");
      if (options) options.hidden = collapsed;
      const change = section.querySelector("[data-change]");
      if (change) change.hidden = !collapsed;

      const chosenIds = new Set(chosen.map((c) => c.variantId));
      section.querySelectorAll(".bundle-product").forEach((tile) => {
        const select = tile.querySelector(".bundle-product__variant");
        const button = tile.querySelector(".bundle-product__pick");
        const id = Number(select ? select.value : button?.dataset.variant);
        const on = chosenIds.has(id);
        tile.classList.toggle("is-chosen", on);
        button?.setAttribute("aria-pressed", String(on));
        if (button) button.textContent = on ? "Added" : "Add";
      });

      const list = section.querySelector("[data-chosen]");
      list.innerHTML = chosen
        .map(
          (c, j) => `
          <li class="bundle-chosen">
            ${c.image ? `<img class="bundle-chosen__image" src="${sized(c.image, 120)}" alt="" width="60" height="60">` : ""}
            <span class="bundle-chosen__title">${escapeHtml(c.title)}</span>
            <button class="bundle-chosen__remove" type="button" data-remove="${j}"
              aria-label="Remove ${escapeHtml(c.title)}">Remove</button>
          </li>`,
        )
        .join("");
      list.querySelectorAll("[data-remove]").forEach((b) =>
        b.addEventListener("click", () => this.remove(card, state, i, Number(b.dataset.remove))),
      );
    });

    const total = state.config.slots.reduce((n, s) => n + s.quantity, 0);
    const filled = state.chosen.reduce((n, c) => n + c.length, 0);
    const complete = state.config.slots.every((s, i) => state.chosen[i].length >= s.quantity);

    // /products.json gives prices as major units ("59.96"), unlike /cart.js
    // which gives cents. Dividing here would have shown 60p for a 60 pound pair.
    const subtotal = state.chosen.flat().reduce((n, c) => n + c.price, 0);
    const saving = complete
      ? this.previewSaving(state.config, subtotal, filled, state.chosen.flat())
      : 0;

    card.querySelector(".bundle-builder__progress").textContent = `${filled} of ${total} chosen`;
    card.querySelector(".bundle-builder__total").textContent = complete
      ? `${this.money.format(subtotal - saving)} — you save ${this.money.format(saving)}`
      : subtotal > 0
        ? `${this.money.format(subtotal)} so far`
        : "";
    card.querySelector(".bundle-builder__add").disabled = !complete;
  }

  /**
   * Display only. The Function decides the real figure from the cart.
   *
   * It has to mirror the Function's logic rather than take a flat percentage,
   * or during the sitewide reprice this would promise "Save 30%" on a basket
   * that only gets about 6.67% more, because the other 25% is already in the
   * shelf price. The rate comes off the item's pre-sale price, so the baseline
   * is the HIGHEST of the live price and compare-at, exactly as the Function
   * takes the highest of price, compare-at and the stored reference price.
   *
   * compare_at is the only part of that the browser can see. Where it is
   * missing or wrong, this under-promises rather than over-promises, which is
   * the right way round for a number on a page.
   */
  previewSaving(config, subtotal, units, chosen) {
    const rate = (config.value || 0) / 100;
    if (!chosen || chosen.length === 0) return subtotal * rate;

    let saving = 0;
    for (const item of chosen) {
      const price = Number(item.price) || 0;
      const compareAt = Number(item.compareAtPrice) || 0;
      const baseline = Math.max(price, compareAt);
      const target = baseline * (1 - rate);
      saving += Math.max(0, price - target);
    }
    return saving;
  }

  async addToCart(card, state) {
    const button = card.querySelector(".bundle-builder__add");
    button.disabled = true;
    button.textContent = "Adding";

    // Choosing the same product twice for a "choose 2" slot is legitimate, so
    // the same variant can appear more than once. Send it as one line with a
    // quantity rather than two lines Shopify would have to reconcile.
    const counts = new Map();
    for (const c of state.chosen.flat()) {
      counts.set(c.variantId, (counts.get(c.variantId) ?? 0) + 1);
    }
    const items = [...counts].map(([id, quantity]) => ({ id, quantity }));

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(await res.text());

      const cart = await fetch("/cart.js").then((r) => r.json());
      // The theme's own drawer contract. Themes without it still got the items.
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", { bubbles: true, detail: { cart, openMiniCart: true } }),
      );

      button.textContent = "Added";
      state.chosen = state.config.slots.map(() => []);
      this.refresh(card, state);
      setTimeout(() => (button.textContent = "Add bundle to basket"), 2000);
    } catch (error) {
      button.textContent = "Could not add, please try again";
      button.disabled = false;
      console.error("Bundle builder could not add to cart", error);
    }
  }
}

/** Shopify CDN sizing, without assuming the URL already carries a query. */
function sized(url, width) {
  if (!url) return "";
  return url + (url.includes("?") ? "&" : "?") + "width=" + width;
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

if (!customElements.get("bundle-list")) customElements.define("bundle-list", BundleList);
